import { memo } from 'react';

import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import useSWRMutation from 'swr/mutation';

import { PartnerCard } from './partnerCard/PartnerCard';

import css from './DraggablePartnerCards.module.css';

import { partnersService } from 'v2/api/partnersService';
import { SortableCard } from 'v2/components/card/sortableCard/SortableCard';
import { useSortableList } from 'v2/hooks/useSortableList';
import { PartnerModel } from 'v2/types/PartnerModel';

interface DraggablePartnerCardsProps {
  partners: PartnerModel[];
  onRevalidate: VoidFunction;
}

export const DraggablePartnerCards = memo<DraggablePartnerCardsProps>(({ partners, onRevalidate }) => {
  const { items, itemIds, sensors, activeItem, onDragStart, onDragEnd, onDragCancel } = useSortableList<PartnerModel>(
    partners,
    {
      onReorder: (next) => {
        trigger(next.map((item, i) => ({ id: item.id, order: i + 1 })));
      },
    }
  );

  const { trigger } = useSWRMutation('changePartnersOrder', partnersService.changePartnersOrder, {
    onSuccess: () => {
      onRevalidate();
    },
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={itemIds}>
        <div className={css.draggablePartnerCards}>
          {items.map((item) => (
            <SortableCard key={item.id} id={item.id}>
              <PartnerCard partner={item} onRevalidate={onRevalidate} />
            </SortableCard>
          ))}
        </div>
      </SortableContext>

      <DragOverlay adjustScale style={{ cursor: 'grabbing' }}>
        {activeItem && <PartnerCard partner={activeItem} onRevalidate={onRevalidate} />}
      </DragOverlay>
    </DndContext>
  );
});

DraggablePartnerCards.displayName = 'PartnerCard';
